import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, CreditCard, AlertCircle, AlertTriangle, Calendar, TrendingUp, TrendingDown, Wallet, Zap, ChevronRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ICON_MAP = {
  CreditCard, AlertCircle, AlertTriangle, Calendar, TrendingUp, TrendingDown, Wallet, Zap, Sparkles,
};

const PRIORITY_RANK = { high: 3, medium: 2, low: 1 };

export default function AssistantPanel() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/assistant/today").then(r => setData(r.data)).catch(() => {});
    // Hide after dismissal in this session
    if (sessionStorage.getItem("td_assistant_dismissed")) setDismissed(true);
  }, []);

  const close = () => {
    sessionStorage.setItem("td_assistant_dismissed", "1");
    setDismissed(true);
  };

  if (!data || dismissed) return null;
  if (!data.insights || data.insights.length === 0) return null;

  const high = data.insights.filter(i => i.priority === "high");
  const others = data.insights.filter(i => i.priority !== "high");
  const featured = high[0] || others[0];
  const list = data.insights.slice(0, 6);

  return (
    <div className="relative rounded-3xl overflow-hidden border border-violet-500/20 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-slate-900/0" data-testid="assistant-panel">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.18),transparent_60%)]"/>
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-500/15 blur-3xl"/>

      <div className="relative p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-[0_4px_20px_-4px_rgba(168,85,247,0.5)]">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.2}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300/80 font-bold">Asistente del día</div>
              <div className="text-base font-bold text-white">{data.greeting}{data.name ? `, ${data.name}` : ""} <span className="text-violet-300">·</span> <span className="text-slate-300 font-normal">{data.total} insight{data.total !== 1 ? "s" : ""} hoy</span></div>
            </div>
          </div>
          <button onClick={close} data-testid="assistant-dismiss" className="text-slate-500 hover:text-white p-1.5 rounded-lg hover:bg-white/5">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-3">
          {/* Featured insight */}
          {featured && (
            <InsightCard insight={featured} featured nav={nav}/>
          )}
          {/* Other insights */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-2.5">
            {list.filter(i => i !== featured).slice(0, 4).map((i, idx) => (
              <InsightCard key={idx} insight={i} nav={nav}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightCard({ insight, featured = false, nav }) {
  const Icon = ICON_MAP[insight.icon] || Sparkles;
  const priorityBg = {
    high: "bg-rose-500/10 border-rose-500/30",
    medium: "bg-amber-500/10 border-amber-500/30",
    low: "bg-white/[0.03] border-white/10",
  }[insight.priority];

  return (
    <button
      onClick={() => insight.action?.route && nav(insight.action.route)}
      data-testid={`insight-${insight.type}`}
      disabled={!insight.action}
      className={`text-left rounded-2xl border ${priorityBg} ${featured ? "p-4" : "p-3"} ${insight.action ? "hover:scale-[1.01] cursor-pointer" : "cursor-default"} transition-transform group`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${insight.color}22` }}>
          <Icon className="w-4 h-4" style={{ color: insight.color }} strokeWidth={2.2}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-white ${featured ? "text-sm" : "text-xs"} leading-tight`}>{insight.title}</div>
          {insight.detail && <div className={`text-slate-400 mt-1 ${featured ? "text-xs" : "text-[11px]"} leading-snug`}>{insight.detail}</div>}
          {insight.action && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-violet-300 group-hover:text-violet-200">
              {insight.action.label} <ChevronRight className="w-3 h-3"/>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
